import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MDBDataTable } from "mdbreact";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { fetchUsers } from "../../../services/userApi";
import { fetchDepartments } from "../../../services/departmentApi";
import { usePermission } from "../../../hooks/usePermission";
import { MODULE_KEYS, ACTIONS } from "../../../constants/permissions";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canAdd = usePermission(MODULE_KEYS.USER, ACTIONS.ADD);
  const canUpdate = usePermission(MODULE_KEYS.USER, ACTIONS.UPDATE);

  useEffect(() => {
    loadUsers();
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]));
  }, []);

  const loadUsers = () => {
    setLoading(true);
    fetchUsers()
      .then(setUsers)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const getDeptName = (deptId) => {
    if (deptId && typeof deptId === "object") {
      return deptId.departmentName ?? deptId.name ?? "—";
    }
    const dept = departments.find((d) => d._id === deptId || d.id === deptId);
    return dept?.departmentName ?? dept?.name ?? "—";
  };

  const data = {
    columns: [
      { label: "#", field: "index", sort: "asc", width: 50 },
      { label: "Name", field: "firstName", sort: "asc" },
      { label: "Email", field: "email", sort: "asc" },
      { label: "Mobile", field: "mobileNumber", sort: "asc" },
      { label: "Role / Dept", field: "role", sort: "asc" },
      { label: "Actions", field: "actions", sort: "disabled" },
    ],
    rows: users.map((user, index) => ({
      index: index + 1,
      firstName: user.firstName || user.first_name || "-",
      email: user.email || "-",
      mobileNumber: user.mobileNumber || user.mobile_number || "-",
      role: (
        <div className="d-flex flex-column gap-1">
          <span className="badge bg-info bg-opacity-10 text-info fw-normal px-2 py-1">
            {user.roleId?.roleName || user.role?.roleName || "—"}
          </span>
          <span className="badge bg-secondary bg-opacity-10 text-secondary fw-normal px-2 py-1">
            {getDeptName(user.departmentId)}
          </span>
        </div>
      ),
      actions: canUpdate ? (
        <Link to={`/user-edit/${user._id}`} className="btn btn-primary btn-sm">
          <i className="fas fa-pencil-alt" />
        </Link>
      ) : (
        <span className="text-muted">-</span>
      ),
    })),
  };

  return (
    <Fragment>
      <PageTitle
        activeMenu="User List"
        motherMenu="Users"
        pageContent="User List"
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Users</h4>
              {canAdd && (
                <Link to="/user-add" className="btn btn-primary btn-sm">
                  <i className="fa fa-plus me-1" /> Add User
                </Link>
              )}
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger">
                  Failed to load users: {error}{" "}
                  <button
                    className="btn btn-sm btn-outline-danger ms-2"
                    onClick={loadUsers}
                  >
                    Retry
                  </button>
                </div>
              )}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading users...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <div className="display mb-4 dataTablesCard customer-list-table">
                    <MDBDataTable data={data} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default UserList;
